import React from 'react';
import { getAllBlockDefinitions } from '../core/registry.js';
import { canDropInto } from '../core/registry.js';
import { RESTRICTED_PARENTS } from '../dnd/rules.js';
import { useDoc } from '../store/editorStore.jsx';

/**
 * Inserter – a searchable popup for adding new blocks.
 *
 * Props:
 *   parentId {string|null}  – container to insert into (null = root)
 *   index    {number}       – insertion position
 *   onInsert (type) => void – called with the selected block type
 *   onClose  ()    => void
 */
export function Inserter({ parentId, index, onInsert, onClose }) {
  const [query, setQuery] = React.useState('');
  const doc = useDoc();

  // Determine the type of the parent container (null = root accepts everything)
  const parentBlock = parentId ? doc.blocks[parentId] : null;
  const parentType = parentBlock ? parentBlock.type : null;

  const defs = getAllBlockDefinitions().filter((def) => {
    const restrictedParent = RESTRICTED_PARENTS[def.type];

    if (restrictedParent) {
      // This block type is only valid inside a specific parent type.
      if (parentType === null) return false;         // not at root
      if (parentType !== restrictedParent) return false; // wrong parent
    } else if (parentType !== null) {
      // Inserting into a container: check that the container allows this child type.
      if (!canDropInto(def.type, parentType)) return false;
    }

    return true;
  });

  const filtered = query
    ? defs.filter(
        (d) =>
          d.ui.label.toLowerCase().includes(query.toLowerCase()) ||
          (d.ui.keywords || []).some((k) => k.toLowerCase().includes(query.toLowerCase())),
      )
    : defs;

  return (
    <div
      className="editor-inserter border rounded shadow bg-white"
      style={{ minWidth: 240, zIndex: 1000 }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="p-2 border-bottom">
        <input
          autoFocus
          type="text"
          className="form-control form-control-sm"
          placeholder="Search blocks…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Escape') onClose(); }}
        />
      </div>
      <div style={{ maxHeight: 280, overflowY: 'auto' }}>
        {filtered.length === 0 ? (
          <div className="p-3 text-muted small text-center">No blocks found.</div>
        ) : (
          filtered.map((def) => (
            <button
              key={def.type}
              type="button"
              className="btn btn-light w-100 text-start rounded-0 py-2 px-3 d-flex align-items-center gap-2"
              style={{ borderBottom: '1px solid #f0f0f0' }}
              onClick={() => { onInsert(def.type); onClose(); }}
            >
              <span style={{ fontSize: '1.2rem', lineHeight: 1 }}>{def.ui.icon}</span>
              <span className="fw-semibold small">{def.ui.label}</span>
            </button>
          ))
        )}
      </div>
    </div>
  );
}
